import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CoreModule, FlexLayoutModule } from '@angular/flex-layout';

import { VirtualScrollerModule } from 'ngx-virtual-scroller';

import { StudiesRoutingModule } from './studies-routing.module';
import { StudiesComponent } from './pages/studies/studies.component';
import { YoutubeDialogComponent } from './components/youtube-dialog/youtube-dialog.component';
import { DashboardComponent } from '../dashboard.component';
import { StatusBadgeColorDirective } from '../directives/status-badge-color/status-badge-color.directive';
import { CreateStudyDialogComponent } from './components/create-study-dialog/create-study-dialog.component';
import { StudyDetailsComponent } from './pages/study-details/study-details.component';
import { ReleasesDialogComponent } from './components/releases-dialog/releases-dialog.component';
import { StudyHomeComponent } from './pages/study-home/study-home.component';
import { ColorPickerComponent } from './components/color-picker/color-picker.component';
import { SubscriptionComponent } from './pages/subscription/subscription.component';
import { AngularMaterialModule } from '../../material/angular-material.module';
import { CoreModule as CustomCoreModule } from '../../core/core.module';
import { SharedModule } from '../../shared/shared.module';
import { InterventionsComponent } from './pages/interventions/interventions.component';
import { InterventionFormComponent } from './pages/interventions/intervention-form/intervention-form.component';
import { InterventionContentComponent } from './pages/interventions/intervention-content/intervention-content.component';
import { InterventionModulesComponent } from './pages/interventions/intervention-modules/intervention-modules.component';
import { InformativePagesComponent } from './pages/informative-pages/informative-pages.component';
import { InformativePagesTemplateComponent } from './components/informative-pages-template/informative-pages-template.component';
import { InformativePageComponent } from './components/informative-pages-template/informative-page/informative-page.component';
import { ModulesComponent } from './pages/modules/modules.component';
import { ModuleFormComponent } from './pages/modules/module-form/module-form.component';
import { StudyResearchersComponent } from './pages/study-researchers/study-researchers.component';
import { StudyResearcherComponent } from './pages/study-researchers/study-researcher/study-researcher.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { SessionStatusComponent } from './pages/interventions/session-status/session-status.component';
import { StudyTypeColorDirective } from '../directives/study-type-color/study-type-color.directive';
import { SessionFilesComponent } from './pages/interventions/session-files/session-files.component';
import { ModuleItemListComponent } from './components/module-item-list/module-item-list.component';
import { ModuleItemDialogComponent } from './components/module-item-dialog/module-item-dialog.component';
import { CalculatedVariablesComponent } from './pages/calculated-variables/calculated-variables.component';
import { CalculatedVariableDialogComponent } from './components/calculated-variable-dialog/calculated-variable-dialog.component';
import { TemplatesComponent } from './pages/templates/templates.component';
import { StatementComponent } from './components/module-item-dialog/module-items/statement/statement.component';
import { ModuleItemPreviewComponent } from './components/module-item-dialog/module-item-preview/module-item-preview.component';
import { TemplateFormComponent } from './pages/templates/template-form/template-form.component';
import { TiimStudyParticipantsComponent } from './pages/study-participants/tiim-study-participants/tiim-study-participants.component';
import { TiimStudyParticipantComponent } from './pages/study-participants/tiim-study-participants/tiim-study-participant/tiim-study-participant.component';
import { CcStudyParticipantsComponent } from './pages/study-participants/cc-study-participants/cc-study-participants.component';
import { DropdownComponent } from './components/module-item-dialog/module-items/dropdown/dropdown.component';
import { ModuleItemCoreComponent } from './components/module-item-dialog/module-items/module-item-core/module-item-core.component';
import { LineInputsComponent } from './components/module-item-dialog/module-items/line-inputs/line-inputs.component';
import { MultipleChoiceComponent } from './components/module-item-dialog/module-items/multiple-choice/multiple-choice.component';
import { YesNoComponent } from './components/module-item-dialog/module-items/yes-no/yes-no.component';
import { NumberInputsComponent } from './components/module-item-dialog/module-items/number-inputs/number-inputs.component';
import { LikertScaleComponent } from './components/module-item-dialog/module-items/likert-scale/likert-scale.component';
import { SectionsComponent } from './pages/templates/sections/sections.component';
import { VideoAudioComponent } from './components/module-item-dialog/module-items/video-audio/video-audio.component';
import { VideoAudioUploadComponent } from './components/module-item-dialog/module-items/video-audio/video-audio-upload/video-audio-upload.component';
import { SectionDialogComponent } from './components/section-dialog/section-dialog.component';
import { DescriptionSectionComponent } from './components/section-dialog/description-section/description-section.component';
import { SectionDialogCoreComponent } from './components/section-dialog/section-dialog-core/section-dialog-core.component';
import { GraphComponent } from './components/module-item-dialog/module-items/graph/graph.component';
import { EmotionQuadrantComponent } from './components/module-item-dialog/module-items/emotion-quadrant/emotion-quadrant.component';
import { SimpleItemsComponent } from './components/module-item-dialog/module-items/simple-items/simple-items.component';
import { OrbitsComponent } from './components/module-item-dialog/module-items/orbits/orbits.component';
import { SubsectionsListComponent } from './components/section-dialog/subsections-list/subsections-list.component';
import { GenericGraphComponent } from './components/section-dialog/generic-graph/generic-graph.component';
import { DetailsPageComponent } from './components/section-dialog/details-page/details-page.component';
import { OrbitsCanvasPreviewComponent } from './components/module-item-dialog/module-item-preview/orbits-canvas-preview/orbits-canvas-preview.component';
import { OrbitsConfigPreviewComponent } from './components/module-item-dialog/module-item-preview/orbits-config-preview/orbits-config-preview.component';
import { NetworkComponent } from './components/module-item-dialog/module-items/network/network.component';
import { NetworkConfigPreviewComponent } from './components/module-item-dialog/module-item-preview/network-config-preview/network-config-preview.component';
import { EmailPagesComponent } from './pages/email-pages/email-pages.component';
import { ProfileComponent } from './components/section-dialog/profile/profile.component';
import { DimensionComponent } from './components/section-dialog/dimension/dimension.component';
import { BarComponent } from './components/section-dialog/bar/bar.component';
import { ProfileConfigurationComponent } from './components/section-dialog/profile-configuration/profile-configuration.component';
import { DimensionConfigurationComponent } from './components/section-dialog/dimension-configuration/dimension-configuration.component';
import { MetricsComponent } from './pages/metrics/metrics.component';
import { MetricsPreviewComponent } from './pages/metrics/metrics-preview/metrics-preview.component';
import { MetricsDialogComponent } from './components/metrics-image-dialog/metrics-dialog.component';
import { UnansweredMetricsPreviewComponent } from './pages/metrics/unanswered-metrics-preview/unanswered-metrics-preview.component';
import { ImportTemplateComponent } from './components/import-template/import-template.component';
import { TimingRulesDialogComponent } from './components/timing-rules-dialog/timing-rules-dialog.component';
import { MetricsDownloadComponent } from './pages/metrics/metrics-download/metrics-download.component';
import { ModuleItemRoutingComponent } from './components/module-item-dialog/module-item-routing/module-item-routing.component';
import { CategoryListDialogComponent } from './components/category-list-dialog/category-list-dialog.component';
import { IvyComponent } from './components/module-item-dialog/module-items/ivy/ivy.component';
import { AudioUploadComponent } from './components/module-item-dialog/module-items/ivy/audio-upload/audio-upload.component';
import { QRCodeModule } from 'angularx-qrcode';
import {NotificationComponent} from "./components/notification/notification.component";
import {ESMCalenderComponent} from "./components/esm-calender/esm-calender.component";
import {CalendarModule} from "angular-calendar";

@NgModule({
  declarations: [
    StudiesComponent,
    YoutubeDialogComponent,
    DashboardComponent,
    StatusBadgeColorDirective,
    CreateStudyDialogComponent,
    StudyDetailsComponent,
    ReleasesDialogComponent,
    StudyHomeComponent,
    ColorPickerComponent,
    SubscriptionComponent,
    InterventionsComponent,
    InterventionFormComponent,
    InterventionContentComponent,
    InterventionModulesComponent,
    InformativePagesComponent,
    InformativePagesTemplateComponent,
    InformativePageComponent,
    ModulesComponent,
    ModuleFormComponent,
    StudyResearchersComponent,
    StudyResearcherComponent,
    ConfirmationDialogComponent,
    SessionFilesComponent,
    SessionStatusComponent,
    StudyTypeColorDirective,
    ModuleItemListComponent,
    ModuleItemDialogComponent,
    CalculatedVariablesComponent,
    CalculatedVariableDialogComponent,
    TemplatesComponent,
    StatementComponent,
    ModuleItemPreviewComponent,
    TemplateFormComponent,
    TiimStudyParticipantsComponent,
    TiimStudyParticipantComponent,
    CcStudyParticipantsComponent,
    DropdownComponent,
    ModuleItemCoreComponent,
    LineInputsComponent,
    NumberInputsComponent,
    MultipleChoiceComponent,
    YesNoComponent,
    LikertScaleComponent,
    VideoAudioComponent,
    VideoAudioUploadComponent,
    SectionsComponent,
    SectionDialogComponent,
    DescriptionSectionComponent,
    SectionDialogCoreComponent,
    GraphComponent,
    SimpleItemsComponent,
    OrbitsComponent,
    OrbitsCanvasPreviewComponent,
    OrbitsConfigPreviewComponent,
    SubsectionsListComponent,
    GenericGraphComponent,
    DetailsPageComponent,
    EmotionQuadrantComponent,
    NetworkComponent,
    NetworkConfigPreviewComponent,
    EmailPagesComponent,
    ProfileComponent,
    DimensionComponent,
    BarComponent,
    ProfileConfigurationComponent,
    MetricsComponent,
    MetricsPreviewComponent,
    DimensionConfigurationComponent,
    MetricsDialogComponent,
    UnansweredMetricsPreviewComponent,
    ImportTemplateComponent,
    TimingRulesDialogComponent,
    MetricsDownloadComponent,
    ModuleItemRoutingComponent,
    CategoryListDialogComponent,
    IvyComponent,
    AudioUploadComponent,
    NotificationComponent,
    ESMCalenderComponent
  ],
  imports: [
    CommonModule,
    StudiesRoutingModule,
    AngularMaterialModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    CoreModule,
    CustomCoreModule,
    SharedModule,
    VirtualScrollerModule,
    QRCodeModule,
    CalendarModule,
  ],
})
export class StudiesModule {}
